/**
 * O extrato do turno.
 *
 * O SINAL de cada linha vem do servidor, no campo `entra` — a tela não sabe
 * que sangria é saída. Reimplementar a regra aqui faria o dia em que um tipo
 * novo aparecer (devolução, quando o módulo existir) render uma linha somando
 * onde deveria subtrair, e o extrato passaria a discordar do total logo acima
 * dele.
 */
import { formatarMoeda } from '../../../plataforma/dinheiro'
import type { Movimento } from '../../../plataforma/caixa'
import { Icone } from '../../../componentes/Icone'
import { Selo } from '../../../componentes/Selo'

export function TabelaMovimentos({ movimentos }: { movimentos: Movimento[] }) {
  if (movimentos.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2.5 px-4 py-8 text-center">
        <Icone nome="receipt_long" tamanho={26} className="text-inativo" />
        <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
          Nenhum movimento ainda. Vendas em dinheiro, sangrias e suprimentos aparecem aqui na
          ordem em que acontecem.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse">
        <caption className="sr-only">Movimentos do turno de caixa</caption>
        <thead>
          <tr className="bg-fundo">
            {['Hora', 'Tipo', 'Descrição', 'Operador', 'Valor'].map((titulo) => (
              <th
                key={titulo}
                scope="col"
                className={`border-b border-borda px-3 py-2.5 text-[12.5px] font-semibold text-texto-suave ${
                  titulo === 'Valor' ? 'text-right' : 'text-left'
                }`}
              >
                {titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {movimentos.map((movimento, indice) => (
            <tr key={`${movimento.criado_em}-${indice}`} className="border-b border-divisoria">
              <td className="tabular px-3 py-2.5 text-[13px] text-texto-suave">
                {movimento.criado_em_hora}
              </td>

              <td className="px-3 py-2.5">
                <Selo tom={movimento.entra ? 'ok' : 'atencao'}>{movimento.tipo_rotulo}</Selo>
              </td>

              <td className="px-3 py-2.5 text-[13px] text-texto">
                {descricaoDe(movimento)}
              </td>

              <td className="px-3 py-2.5 text-[13px] text-texto-suave">
                {movimento.operador || '—'}
                {movimento.autorizador && (
                  <span className="block text-[11.5px] text-texto-fraco">
                    autorizado por {movimento.autorizador}
                  </span>
                )}
              </td>

              {/* O sinal é do servidor, não da tela. */}
              <td
                className={`tabular px-3 py-2.5 text-right text-[13.5px] font-semibold ${
                  movimento.entra ? 'text-ok' : 'text-erro'
                }`}
              >
                {movimento.entra ? '+' : '−'} {formatarMoeda(movimento.valor_centavos)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * A descrição legível de um movimento.
 *
 * A venda mostra o código do cupom, porque é por ele que a conferência
 * encontra a operação; a sangria mostra o destino, que é a primeira pergunta
 * de quem investiga uma quebra.
 */
function descricaoDe(movimento: Movimento): string {
  if (movimento.venda_codigo) {
    return `Venda ${movimento.venda_codigo}`
  }
  if (movimento.destino) {
    return movimento.descricao
      ? `${movimento.destino} — ${movimento.descricao}`
      : `Para ${movimento.destino}`
  }
  return movimento.descricao || '—'
}
